/**
 * Volumetric cloud renderer using IQ-style FBM approach.
 *
 * Renders as an in-scene sphere mesh (WebGPU-compatible).
 * Uses hash-based 3D value noise (no textures),
 * FBM density with height gradient, and directional derivative lighting
 * (single extra sample instead of expensive light march).
 */

import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useUIStore } from '@/state/useUIStore';
import { calculateSunColor, calculateDayFactor, calculateSunsetFactor } from '@/utils/lighting';
import { cloudVertexShader, cloudFragmentShader } from './shaders';
import { QUALITY_PRESETS } from './types';
import type { CloudsProps } from './types';

export function CloudRenderer({
  radius,
  sunPosition,
  coverage = 0.08,
  density = 1.0,
}: CloudsProps) {
  const meshRef = useRef<THREE.Mesh>(null);

  const cloudsEnabled = useUIStore((s) => s.settings.cloudsEnabled);
  const graphicsQuality = useUIStore((s) => s.settings.graphicsQuality);

  const quality = QUALITY_PRESETS[graphicsQuality] ?? QUALITY_PRESETS.medium;

  // Cloud shell radii — above terrain peaks (maxElev ~0.09, peak ~0.26 of radius)
  const innerRadius = radius * 1.30;
  const outerRadius = radius * 1.35;
  const shellThickness = outerRadius - innerRadius;

  // Noise frequency: one cloud feature ≈ 2x shell thickness
  const noiseFreq = 1.0 / (shellThickness * 2.0);

  const timeRef = useRef(0);

  // Cloud material
  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader: cloudVertexShader,
      fragmentShader: cloudFragmentShader,
      uniforms: {
        uTime:             { value: 0.0 },
        uSunDirection:     { value: new THREE.Vector3(0.5, 0.3, 0.8).normalize() },
        uSunColor:         { value: new THREE.Vector3(1.0, 0.98, 0.95) },
        uCloudInnerRadius: { value: innerRadius },
        uCloudOuterRadius: { value: outerRadius },
        uCoverage:         { value: coverage },
        uDensityScale:     { value: density },
        uCoverageThreshold: { value: 0.65 },
        uNoiseFreq:        { value: noiseFreq },
        uWindOffset:       { value: new THREE.Vector3(0, 0, 0) },
        uStepCount:        { value: quality.raySteps },
        uAtmosphereColor:  { value: new THREE.Vector3(0.55, 0.70, 0.88) },
        uDayFactor:        { value: 1.0 },
      },
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, coverage, density]);

  // Sync quality settings
  useEffect(() => {
    material.uniforms.uStepCount.value = quality.raySteps;
  }, [material, quality]);

  // Update uniforms each frame
  useFrame((_state, delta) => {
    if (!material) return;

    // Time and wind (slow drift in noise-space)
    timeRef.current += delta;
    const windSpeed = 0.005;
    const windTime = timeRef.current * windSpeed;
    material.uniforms.uTime.value = timeRef.current;
    material.uniforms.uWindOffset.value.set(windTime, 0, windTime * 0.6);

    // Sun — use local sun height (dot of sun dir and camera surface normal)
    material.uniforms.uSunDirection.value.copy(sunPosition).normalize();
    const camPos = _state.camera.position;
    const camLen = camPos.length();
    const h = camLen > 0
      ? (sunPosition.x * camPos.x + sunPosition.y * camPos.y + sunPosition.z * camPos.z) / camLen
      : sunPosition.y;
    const [sr, sg, sb] = calculateSunColor(h);
    material.uniforms.uSunColor.value.set(sr, sg, sb);

    // Atmosphere color: blend sky horizon color with sunset warmth
    const day = calculateDayFactor(h);
    const sunset = calculateSunsetFactor(h);
    // Day sky: cool blue horizon
    const dr = 0.55, dg = 0.70, db = 0.88;
    // Sunset: warm orange-pink
    const ssr = 0.85, ssg = 0.45, ssb = 0.25;
    // Blend based on sunset factor
    const ar = dr + (ssr - dr) * sunset;
    const ag = dg + (ssg - dg) * sunset;
    const ab = db + (ssb - db) * sunset;
    material.uniforms.uAtmosphereColor.value.set(ar, ag, ab);
    material.uniforms.uDayFactor.value = day;
  });

  // Dispose on unmount
  useEffect(() => {
    return () => { material.dispose(); };
  }, [material]);

  if (!cloudsEnabled) return null;

  return (
    <mesh ref={meshRef} renderOrder={2}>
      <icosahedronGeometry args={[outerRadius, 5]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}
